import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Video, Plus, Clock, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatTile, Button, Loading, EmptyState, Tag, Avatar } from "@/components/brand";
import { canJoin, formatDayTime, formatR } from "@/lib/format";

export default function TrainerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const dash = useQuery({ queryKey: ["trainer", "dashboard"], queryFn: () => api("/trainer/dashboard") });
  if (dash.isLoading) return <Loading />;
  const d = dash.data || {};
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div data-testid="trainer-dashboard-screen" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-ink tracking-tight">{greet}, <span className="text-brand">{d.name?.split(" ")[0]}</span></h1>
          <p className="font-body text-mutedgrey mt-0.5">Here's your business at a glance.</p>
        </div>
        <Button testID="dashboard-create-class" title="Create Class" icon={Plus} onClick={() => navigate("/trainer/schedule")} />
      </div>

      {d.status !== "approved" ? (
        <div className="p-4 rounded-xl2 bg-brand-soft text-ink font-body text-sm" data-testid="trainer-pending-banner">
          Your trainer profile is <b>{d.status}</b>. An admin needs to approve it before you can create classes and take bookings.
        </div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        <StatTile testID="stat-revenue" label="Revenue this month" value={formatR(d.month?.revenue)} />
        <StatTile testID="stat-sessions" label="Sessions" value={d.month?.sessions ?? 0} />
        <StatTile testID="stat-clients" label="Clients" value={d.month?.clients ?? 0} />
        <StatTile testID="stat-rating" label="Rating" value={Number(d.month?.rating ?? 0).toFixed(1)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <div className="p-4 rounded-xl2 bg-ink text-white flex items-center gap-4">
          <Wallet size={28} className="text-brand" />
          <div className="flex-1"><div className="font-body text-xs text-white/70">Available to withdraw</div><div className="font-display text-2xl">{formatR(d.wallet?.available)}</div></div>
          <Link to="/trainer/earnings"><Button testID="dashboard-earnings" title="Earnings" variant="dark" small /></Link>
        </div>
      </div>

      <div>
        <h3 className="font-display text-xl text-ink mb-3">Today's Sessions</h3>
        {(d.today ?? []).length === 0 ? <EmptyState icon={Clock} title="Nothing scheduled today" subtitle="Enjoy the rest, or add a class." /> : (
          <div className="space-y-2">
            {d.today.map((s) => {
              const joinable = canJoin(s.start_time, s.duration_min);
              return (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl2 border border-divider">
                  <Tag label={s.kind === "private" ? "Private" : "Group"} tone={s.kind === "private" ? "dark" : "pink"} />
                  <div className="flex-1 min-w-0"><div className="font-display text-base text-ink truncate">{s.title}</div><div className="font-body text-xs text-mutedgrey">{formatDayTime(s.start_time)} · {s.kind === "group" ? `${s.booked_count} booked` : s.user_name}</div></div>
                  {joinable ? <Button testID={`dashboard-join-${s.id}`} title="Go Live" icon={Video} small onClick={() => navigate(`/live/${s.kind === "group" ? s.id : s.id}`)} /> : null}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {(d.upcoming_classes ?? []).length > 0 ? (
        <div>
          <h3 className="font-display text-xl text-ink mb-3">Upcoming Classes</h3>
          <div className="space-y-2">
            {d.upcoming_classes.map((c) => (
              <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl2 border border-divider">
                <img src={c.image} alt="" className="w-12 h-12 rounded-md object-cover" />
                <div className="flex-1 min-w-0"><div className="font-display text-base text-ink truncate">{c.title}</div><div className="font-body text-xs text-mutedgrey">{formatDayTime(c.start_time)} · {c.booked_count}/{c.max_participants} booked</div></div>
                <span className="font-display text-brand">{formatR(c.price)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
